/**
 * 消息详情弹窗
 * @author ritaxxu
 */
import React from 'react'
import { Modal, Button, Tag, Divider, message as antdMessage } from 'antd'
import { DownloadOutlined, PaperClipOutlined } from '@ant-design/icons'
import { cosGetObjectUrl } from '@library/cosUtils'
import parse from 'html-react-parser'
import { sanitizeHtml } from '@library/sanitize'
import {
  IInnerMsgItem,
  IInnerMsgAttachment,
  InnerMsgLevel,
  InnerMsgReadStatus,
} from '@types'

interface MessageDetailModalProps {
  visible: boolean
  message: IInnerMsgItem | null
  onClose: () => void
  /** 重要消息的【标为已读】按钮回调（仅 level=2 且未读时显示） */
  onMarkRead: () => void
}

/** 字节转可读大小 */
function formatFileSize (size: number): string {
  if (!size || size <= 0) return '-'
  if (size < 1024) return `${size}B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`
  return `${(size / 1024 / 1024).toFixed(1)}MB`
}

/** 下载附件（走 COS 签名 URL） */
async function downloadAttachment (att: IInnerMsgAttachment) {
  const [url, err] = await cosGetObjectUrl(att.cosKey)
  if (err || !url) {
    antdMessage.error(`下载失败：${err?.message || '获取地址失败'}`)
    return
  }
  const a = document.createElement('a')
  a.href = url
  a.download = att.name
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const MessageDetailModal: React.FC<MessageDetailModalProps> = ({
  visible,
  message,
  onClose,
  onMarkRead,
}) => {
  if (!message) return null

  const isImportant = message.level === InnerMsgLevel.Important
  const isUnread = message.readStatus === InnerMsgReadStatus.Unread

  return (
    <Modal
      title={message.title}
      open={visible}
      onCancel={onClose}
      width={600}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {message.readTime && (
              <span style={{ color: '#999', fontSize: 12 }}>
                阅读时间：{message.readTime}
              </span>
            )}
          </div>
          <div>
            {isImportant && isUnread && (
              <Button
                type="primary"
                danger
                onClick={() => {
                  onMarkRead()
                  onClose()
                }}
              >
                标为已读
              </Button>
            )}
            <Button onClick={onClose} style={{ marginLeft: 8 }}>关闭</Button>
          </div>
        </div>
      }
    >
      <div className="notification-detail">
        <div style={{ marginBottom: 12 }}>
          {isImportant && <Tag color="red">重要</Tag>}
          {message.sourceType === 2 && message.sourceRuleName && (
            <Tag color="blue">{message.sourceRuleName}</Tag>
          )}
          <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>
            {message.sentAt}
          </span>
        </div>
        <Divider style={{ margin: '12px 0' }} />
        <div
          className="notification-detail-content"
        >
          {parse(sanitizeHtml(message.content))}
        </div>

        {/* 附件列表 */}
        {message.attachments && message.attachments.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>附件</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {message.attachments.map((att) => (
                <div
                  key={att.cosKey}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 12px',
                    background: '#f9fafb',
                    borderRadius: 6,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <PaperClipOutlined style={{ color: '#999', marginRight: 8 }} />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginRight: 12,
                      }}
                      title={att.name}
                    >
                      {att.name}
                    </span>
                    <span style={{ color: '#999', fontSize: 12, flexShrink: 0 }}>
                      {formatFileSize(att.size)}
                    </span>
                  </div>
                  <Button
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadAttachment(att)}
                  >
                    下载
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default MessageDetailModal
